/**
 * Microsoft Clarity Analytics Integration with Sentry
 * Enhanced version for Next.js web app
 */

import * as Sentry from '@sentry/react'
import invariant from 'tiny-invariant'

// Define GDPR cookie name locally to avoid cyclic dependency
const GDPR_COOKIE_NAME = 'cfGdpr'

const CLARITY_PROJECT_ID = process.env.NEXT_PUBLIC_CLARITY_KEY
invariant(CLARITY_PROJECT_ID, 'NEXT_PUBLIC_CLARITY_KEY is not set')

declare global {
  interface Window {
    clarity?: {
      consent?: (consent: boolean) => void
      identify?: (userId: string) => void
    }
  }
}

/**
 * Get cookie value by name
 */
function getCookieValue(cookieName: string): string | undefined {
  if (typeof document === 'undefined') {
    return undefined
  }

  const cookies = document.cookie.split('; ')
  // oxlint-disable-next-line typescript/prefer-for-of
  for (let i = 0; i < cookies.length; i++) {
    const parts = cookies[i].split('=')
    if (parts[0] === cookieName) {
      return decodeURIComponent(parts[1] || '')
    }
  }
  return undefined
}

/**
 * Check if user has given GDPR consent
 */
function hasGdprConsent(): boolean {
  const consentCookie = getCookieValue(GDPR_COOKIE_NAME)
  return consentCookie === '1'
}

/**
 * Get current Clarity session information from cookies.
 */
function getClaritySessionInfo() {
  try {
    const clsk = getCookieValue('_clsk')
    const clck = getCookieValue('_clck')

    if (clsk && clck) {
      // Extract the core values - Clarity can use either ^ or | as delimiter
      // Try ^ first (more common), then fallback to |
      // _clsk format: sessionId^timestamp^pageViews^etc OR sessionId|timestamp|pageViews|etc
      // _clck format: userId^timestamp^etc OR userId|timestamp|etc
      const sessionParts = clsk.includes('^') ? clsk.split('^') : clsk.split('|')
      const userParts = clck.includes('^') ? clck.split('^') : clck.split('|')

      const sessionId = sessionParts[0]?.trim()
      const userId = userParts[0]?.trim()

      if (sessionId && sessionId.length > 0 && userId && userId.length > 0) {
        return {
          sessionId,
          userId,
          sessionUrl: `https://clarity.microsoft.com/player/${CLARITY_PROJECT_ID}/${userId}/${sessionId}`,
          projectId: CLARITY_PROJECT_ID,
          userCookie: clck,
        }
      }
    }

    return null
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to parse Clarity session info:', error)
    return null
  }
}

/**
 * Set up the integration between Clarity and Sentry.
 */
function setupClaritySentryIntegration(userId?: string) {
  // Set up Sentry event processor to add dynamic Clarity information
  Sentry.addEventProcessor((event: Sentry.Event) => {
    const clarityInfo = getClaritySessionInfo()

    if (clarityInfo) {
      event.tags = {
        ...event.tags,
        clarity_session: clarityInfo.sessionId,
        clarity_user: clarityInfo.userId,
        clarity_session_url: clarityInfo.sessionUrl,
        clarity_project_id: clarityInfo.projectId,
      }
    }

    // Add Clarity project context
    event.contexts = {
      ...event.contexts,
      clarity: {
        project_id: CLARITY_PROJECT_ID,
        user_id: userId || clarityInfo?.userId || null,
        session_id: clarityInfo?.sessionId || null,
        session_url: clarityInfo?.sessionUrl || null,
        user_cookie: clarityInfo?.userCookie || null,
      },
    }

    return event
  })

  // Set initial scope tags
  updateSentryWithClarityInfo(userId)
}

/**
 * Manually update Sentry with current Clarity session information.
 */
function updateSentryWithClarityInfo(userId?: string) {
  try {
    Sentry.withScope((scope: Sentry.Scope) => {
      const clarityInfo = getClaritySessionInfo()

      // Add Clarity project ID as a tag
      if (CLARITY_PROJECT_ID) {
        scope.setTag('clarity_project_id', CLARITY_PROJECT_ID)
      }

      if (userId) {
        scope.setTag('clarity_user_id', userId)
      }

      if (clarityInfo) {
        scope.setTag('clarity_session', clarityInfo.sessionId)
        scope.setTag('clarity_user', clarityInfo.userId)
        scope.setTag('clarity_session_url', clarityInfo.sessionUrl)

        // Update user context with comprehensive Clarity session info
        scope.setContext('clarity_session', {
          url: clarityInfo.sessionUrl,
          session_id: clarityInfo.sessionId,
          user_id: clarityInfo.userId,
          project_id: clarityInfo.projectId,
          user_cookie: clarityInfo.userCookie,
        })
      }
    })
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('Failed to update Sentry with Clarity info:', error)
  }
}

/**
 * Initialize Sentry integration with Clarity (CDN-only approach)
 */
export async function initClarityWithSentry(userId?: string) {
  if (!CLARITY_PROJECT_ID) {
    return
  }

  try {
    // Wait for Clarity to be available from CDN script
    const waitForClarity = () => {
      // oxlint-disable-next-line promise/avoid-new
      return new Promise<void>(resolve => {
        if (typeof window !== 'undefined' && window.clarity) {
          resolve()
        } else {
          setTimeout(() => waitForClarity().then(resolve), 100)
        }
      })
    }

    await waitForClarity()

    // Handle GDPR consent via global clarity object
    if (hasGdprConsent()) {
      window.clarity?.consent?.(true)
    }

    // Identify user if provided
    if (userId) {
      window.clarity?.identify?.(userId)
    }

    // Set up Sentry integration
    setupClaritySentryIntegration(userId)
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize Clarity with Sentry:', error)
  }
}

/**
 * Update Sentry with current Clarity session information
 */
export function updateSentryWithClaritySession(userId?: string) {
  updateSentryWithClarityInfo(userId)
}
